import React, { useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "Common/Globle/Spinner";
import AddParkingBookingForm from "./Componates/AddParkingBookingForm";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import useFetchAllDataByID from "Hooks/useFetchAllDataById";
import feilds from "../json/BookingFeilds.json";
import { validateForm } from "utils/validation";
import { ParkingBookingRules } from "validation/ValidationRules";
import {
  formatDateForInput,
  calculateCheckoutDate,
  extractHoursFromDuration,
  calculateDurationInHours
} from "../Helper"
import {
  Create,
  GetAllBookingNumber,
  GetAllVehicleType,
  GetZoneTypeById,
  GetSlotTimeById,
  GetSlotTypeById,
  GetSettingById
} from "adminApi/ParkingBookingApi/ParkingBooking";

const AddParkingBooking = () => {
  const navigate = useNavigate();
  const { formdata, otherdata } = feilds;

  const allFields = useMemo(
    () => [...formdata, ...otherdata],
    []
  );

  const initialValues = useMemo(() => {
    return allFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, {});
  }, [allFields]);

  const {
    values,
    errors,
    handleChange,
    setErrors,
    setValues
  } = useFormChange(initialValues, ParkingBookingRules);

  // Fetch booking numbers
  const {
    data: BookingNumberOptions,
    loading: loadingBookingNumberOptions
  } = useFetchAllData(GetAllBookingNumber);

  // Fetch vehicle types
  const {
    data: VehicleOptions,
    loading: loadingVehicleOptions
  } = useFetchAllData(GetAllVehicleType);

  // Fetch zone types based on vehicle selection
  const {
    data: ZoneTypeOptions,
    loading: loadingZoneTypeOptions
  } = useFetchAllDataByID(GetZoneTypeById, values.vehicle_list_id || null);

  // Fetch settings based on setting_id
  const {
    data: SettingOptions,
    loading: loadingSettingOptions
  } = useFetchAllDataByID(GetSettingById, values.setting_id || null);

  // Memoize slot type parameters
  const slotTypeParams = useMemo(() => {
    if (!values.zone_list_id || !values.checkin_date || !values.checkout_date) {
      return null;
    }
    return {
      zone_list_id: values.zone_list_id,
      check_in: values.checkin_date,
      check_out: values.checkout_date
    };
  }, [values.zone_list_id, values.checkin_date, values.checkout_date]);

  // Fetch slot types
  const {
    data: SlotTypeOptions,
    loading: loadingSlotTypeOptions
  } = useFetchAllDataByID(GetSlotTypeById, slotTypeParams);

  // Memoize slot time parameters
  const slotTimeParams = useMemo(() => {
    if (!values.zone_list_id || !values.vehicle_list_id) {
      return null;
    }
    return {
      zone_id: values.zone_list_id,
      vehicle_id: values.vehicle_list_id
    };
  }, [values.zone_list_id, values.vehicle_list_id]);

  // Fetch slot time options
  const {
    data: SlotTimeOptions,
    loading: loadingSlotTimeOptions
  } = useFetchAllDataByID(GetSlotTimeById, slotTimeParams);

  // Auto-calculate checkout date when checkin or time duration changes
  useEffect(() => {
    if (!values.checkin_date || !values.setting_id || !SlotTimeOptions) {
      return;
    }

    const selectedTime = SlotTimeOptions.find(
      option => option.setting_id === values.setting_id
    );

    if (!selectedTime?.hours) {
      return;
    }

    const allowedHours = extractHoursFromDuration(selectedTime.hours);
    const newCheckoutDate = calculateCheckoutDate(values.checkin_date, allowedHours);
    const formattedCheckoutDate = formatDateForInput(newCheckoutDate);

    setValues(prev => ({
      ...prev,
      checkout_date: formattedCheckoutDate
    }));

    setErrors(prev => ({
      ...prev,
      checkout_date: ""
    }));
  }, [values.checkin_date, values.setting_id, SlotTimeOptions]);

  // Validate checkout date against allowed duration
  useEffect(() => {
    if (!values.checkin_date || !values.checkout_date || !values.setting_id || !SlotTimeOptions) {
      return;
    }

    const durationHours = calculateDurationInHours(values.checkin_date, values.checkout_date);

    const selectedTime = SlotTimeOptions.find(
      option => option.setting_id === values.setting_id
    );

    if (!selectedTime?.hours) {
      return;
    }

    const allowedHours = extractHoursFromDuration(selectedTime.hours);

    if (durationHours > allowedHours) {
      const correctedCheckoutDate = calculateCheckoutDate(values.checkin_date, allowedHours);
      const formattedCheckoutDate = formatDateForInput(correctedCheckoutDate);

      setValues(prev => ({
        ...prev,
        checkout_date: formattedCheckoutDate
      }));

      setErrors(prev => ({
        ...prev,
        checkout_date: `Duration adjusted to ${allowedHours} hour(s) as per selected time.`
      }));

      const errorTimeout = setTimeout(() => {
        setErrors(prev => ({
          ...prev,
          checkout_date: ""
        }));
      }, 3000);

      return () => clearTimeout(errorTimeout);
    }
  }, [values.checkout_date, values.checkin_date, values.setting_id, SlotTimeOptions]);

  // Handle form submission — send as JSON, serialize slot_list_id array as JSON string
  const handleFormSubmit = useCallback(async (formValues) => {
    const payload = { ...formValues };
    // slot_list_id is an array — keep as-is, backend handles JSON.parse
    return await Create({ data: payload });
  }, []);

  // Validate form before submission
  const validateBeforeSubmit = useCallback((vals) => {
    const err = validateForm(vals, ParkingBookingRules);
    setErrors(err);
    return err;
  }, [setErrors]);

  // Navigate back on success
  const onSuccessCallback = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const {
    loading: loadingSubmit,
    handleSubmit
  } = useFormSubmit(
    handleFormSubmit,
    validateBeforeSubmit,
    onSuccessCallback,
    "Booking Created successfully"
  );

  // Aggregate loading states
  const isLoading =
    loadingSubmit ||
    loadingVehicleOptions ||
    loadingBookingNumberOptions ||
    loadingZoneTypeOptions ||
    loadingSlotTimeOptions ||
    loadingSlotTypeOptions ||
    loadingSettingOptions;

  return (
    <>
      {isLoading && <Spinner isSpinner={true} />}
      <AddParkingBookingForm
        fields={formdata}
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        title="Create Booking"
        BookingNumberOptions={BookingNumberOptions}
        VehicleOptions={VehicleOptions}
        ZoneTypeOptions={ZoneTypeOptions}
        SlotTimeOptions={SlotTimeOptions}
        SlotTypeOptions={SlotTypeOptions}
        SettingOptions={SettingOptions}
      />
    </>
  );
};

export default AddParkingBooking;